import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EasyKalaEnvironment } from '../../../environments/environment.model';
import { DeviceIdService } from '../device/device-id.service';

/** Identifies this client to the kala backend. */
export const KALA_APP_NAME = 'kala-mobile';

export const APP_NAME_HEADER = 'x-app-name';
export const DEVICE_ID_HEADER = 'x-device-id';

/**
 * Stamps `x-app-name` and `x-device-id` on every request to a kala backend.
 *
 * Both are app-wide identity, not per-call detail, so they belong here rather
 * than in each service. The device id is the SHA-256 of this install's push
 * unique device id, already computed at startup by `provideDeviceId` — see
 * {@link DeviceIdService}. When it could not be computed the header is left
 * off entirely; an empty `x-device-id` would read as a real, shared device.
 *
 * Only kala's own origins are stamped. Neither header is CORS-safelisted, so
 * adding one turns a plain cross-origin request into a preflighted one, and a
 * host that does not name it in `Access-Control-Allow-Headers` rejects the
 * request outright. kala shares its `HttpClient` with `@authentication`, which
 * talks to MTS — a backend this ticket does not reach and which would fail that
 * preflight. Same-origin requests are always stamped: they are kala's own, and
 * the browser does not preflight them.
 *
 * A header a caller set itself wins, so a request that needs to speak for
 * another app or device can still say so.
 */
export const AppHeadersInterceptor: HttpInterceptorFn = (req, next) => {
	const environment = inject(EasyKalaEnvironment);

	if (!isKalaBackend(req.url, environment)) {
		return next(req);
	}

	const deviceId = inject(DeviceIdService).deviceId();

	let headers = req.headers;

	if (!headers.has(APP_NAME_HEADER)) {
		headers = headers.set(APP_NAME_HEADER, KALA_APP_NAME);
	}

	if (deviceId && !headers.has(DEVICE_ID_HEADER)) {
		headers = headers.set(DEVICE_ID_HEADER, deviceId);
	}

	return next(req.clone({ headers }));
};

/** @returns whether `url` addresses the kala API, the kala SSE gateway, or the app itself. */
function isKalaBackend(url: string, environment: EasyKalaEnvironment): boolean {
	const target = absoluteOrigin(url);

	if (!target) {
		// A relative URL — the app's own origin, so it is kala by definition.
		return true;
	}

	return [environment.setting?.kalaApiPath, environment.setting?.sseApiPath]
		.map(absoluteOrigin)
		.some(origin => origin === target);
}

/** @returns the origin of an absolute URL, or `null` when `url` is relative, empty, or unset. */
function absoluteOrigin(url: string | undefined): string | null {
	if (!url) {
		return null;
	}

	try {
		// A relative URL has no origin of its own, so this throws — which is the answer.
		return new URL(url).origin;
	} catch {
		return null;
	}
}
